import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
  fetchCustomerPortalLink,
  fetchMondiaCustomerPortalUrl,
  fetchRecurlyCustomerPortalUrl,
  fetchSubscriptionPacks,
  getStripeRecurring,
  type GetStripeRecurringProps,
  getSubscriptionDetails,
  type IGetStripeRecurringResponse,
  type IGetSubscriptionDetailsProps,
  type SubscriptionPack,
} from '../../api/subscription';

type SubscriptionDetails = Awaited<ReturnType<typeof getSubscriptionDetails>>;

export const useSubscriptionDetails = (
  props: IGetSubscriptionDetailsProps,
  isAuthenticated: boolean,
  subscriptionName?: string,
): UseQueryResult<SubscriptionDetails, AxiosError> =>
  useQuery({
    queryKey: ['getSubscriptionDetails', props],
    queryFn: () => getSubscriptionDetails(props),
    enabled: isAuthenticated && !!props.organization_id,
    refetchInterval: ({ state: { data: subscriptionData } }) => {
      return subscriptionName &&
        subscriptionData?.billingDetails.planName !== subscriptionName
        ? 20000
        : false;
    },
  });

export const useRecurlyCustomerPortalUrl = (enabled: boolean) =>
  useQuery({
    queryKey: ['getRecurlyCustomerPortalUrl'],
    queryFn: () => fetchRecurlyCustomerPortalUrl(),
    enabled: enabled,
  });

export const useMondiaCustomerPortalUrl = (enabled: boolean) =>
  useQuery({
    queryKey: ['getMondiaCustomerPortalUrl'],
    queryFn: () => fetchMondiaCustomerPortalUrl(),
    enabled,
  });

export const useStripeRecurring = () =>
  useQuery({
    queryKey: ['getStripeRecurring'],
    queryFn: () => getStripeRecurring(),
    networkMode: 'always',
  });

type CustomerPortalLink = Awaited<ReturnType<typeof fetchCustomerPortalLink>>;
export const useCustomerPortalLink = ({
  token,
}: GetStripeRecurringProps): UseQueryResult<CustomerPortalLink, AxiosError> =>
  useQuery({
    queryKey: ['getCustomerPortalLink', token],
    queryFn: () => fetchCustomerPortalLink(),
    enabled: false,
  });

export const useSubscriptionProductPacks = () =>
  useQuery({
    queryKey: ['getSubscriptionProductPacks'],
    queryFn: () => fetchSubscriptionPacks(),
    networkMode: 'always',
  });

/**
 * Combined hook for SubscriptionPage that fetches all subscription-related data
 * Combines: subscription details, pricing plans, subscription packs, and customer portal link
 */
export interface UseSubscriptionPageDataProps {
  orgId: string;
  isAuthenticated: boolean;
  userToken: string;
  purchasedSubscriptionName: string;
}

export interface UseSubscriptionPageDataResult {
  subscriptionData: SubscriptionDetails | undefined;
  pricingPlans: IGetStripeRecurringResponse['data'];
  subscriptionPacks: SubscriptionPack[] | undefined;
  customLinkData: CustomerPortalLink | undefined;
  isLoading: boolean;
  isError: boolean;
  errors: {
    subscriptionError?: AxiosError | null;
    customerLinkError?: AxiosError | null;
  };
  refetchCustomerLink: () => Promise<any>;
}

export const useSubscriptionPageData = ({
  orgId,
  isAuthenticated,
  userToken,
  purchasedSubscriptionName,
}: UseSubscriptionPageDataProps): UseSubscriptionPageDataResult => {
  const {
    data: subscriptionData,
    isError: isSubscriptionError,
    error: subscriptionError,
    isLoading: isSubscriptionLoading,
  } = useSubscriptionDetails(
    { organization_id: orgId },
    isAuthenticated,
    purchasedSubscriptionName,
  );

  const { data: pricingPlansResponse, isLoading: isPricingPlansLoading } =
    useStripeRecurring();

  const {
    data: subscriptionPacksResponse,
    isLoading: isSubscriptionPacksLoading,
  } = useSubscriptionProductPacks();

  const {
    data: customLinkData,
    isError: isCustomerLinkError,
    error: customerLinkError,
    isLoading: isCustomerLinkLoading,
    refetch: refetchCustomerLink,
  } = useCustomerPortalLink({ token: userToken });

  return {
    subscriptionData,
    pricingPlans: pricingPlansResponse?.data || [],
    subscriptionPacks: subscriptionPacksResponse?.data,
    customLinkData,
    isLoading:
      isSubscriptionLoading ||
      isPricingPlansLoading ||
      isSubscriptionPacksLoading ||
      isCustomerLinkLoading,
    isError: isSubscriptionError || isCustomerLinkError,
    errors: {
      subscriptionError,
      customerLinkError,
    },
    refetchCustomerLink,
  };
};
